import { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import _ from 'lodash'
import { Loader } from '@vzmi/shp_shared_compo'
import { ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from 'recharts'
import { httpHandler } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { FetchDataUtil } from '@vzmi/component_ec_nodejs_util'
import Tabs from '../../../Common/NewDashboard/Tabs/Tabs'
import Block from '../../../Common/NewDashboard/Block/Block'
import Overview, { MemberOverviewDescription } from './Overview'
import TaiwanMaps from './TaiwanMaps'
import { Error } from '../../../Common/StatePage'
import Information from '../../../Common/Information'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper from '../../../../../lib/errorHelper'
import { getUIFormatDate, DATE_FORMAT, getFormatData } from '../../../../lib/MomentHelper'
import { EMPTY_VALUE } from '../../../../constants/globalTypes'
import SearchForm, { CONFIG } from './SearchForm/SearchForm'
import TrendCharts, { FIELD as TRANSFORMED_FIELD } from './TrendCharts/TrendCharts'
import { DEFAULT_DASHBOARD_BESTSELLERS_DATE_RANGE } from '../../../../constants/date'
import styles from './MemberAnalyses.css'

const MemberAnalyses = props => {
  const onTabClick = (tabKey) => {
    const i13nModel = {
      action: 'center_supplierDashboard',
      category: 'supplierCenter',
      label: tabKey
    }
    props.sendDashboardPageview(i13nModel)
  }
  return (
    <div
      data-role='member-analysis'
      className={styles.memberAnalysis}
    >
      <Loader loaded={!false}>
        <Tabs
          type='tabs'
          onTabClick={onTabClick}
          items={[
            {
              label: '整體概況',
              key: 'member_overviewtab',
              children: (
                <Overview {...props} />
              )
            },
            {
              label: '買家所在分佈',
              key: 'member_locationtab',
              children: (
                <BuyerDistribution {...props} />
              )
            },
            {
              label: '會員趨勢分析',
              key: 'member_trendtab',
              children: (
                <MemberTrend {...props} />
              )
            }]}
        />
      </Loader>
    </div>
  )
}

// sort data by memberQuantity and add fill color
export const transformToMapData = data =>
  data.sort((a, b) => b.memberQuantity - a.memberQuantity).map((item, i) => {
    return {
      ...item,
      // colored #6002D2 but alpha from 1 to lighter each times
      fill: `rgba(136, 59, 198, ${1 - (i * 0.03)})`
    }
  })

const BuyerDistribution = ({ fetchMemberAnalyses }) => {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState(null)

  const search = useCallback(async () => {
    setIsLoading(true)
    const [reqError, payload = {}] = await safeAwait(
      fetchMemberAnalyses({ group: 'buyerDistribution' }).then(httpHandler)
    )
    setIsLoading(false)
    const errorMessage = errorHelper.parseErrorMessage(reqError)
    setFetchError(errorMessage)
    if (reqError) {
      return
    }
    const { analyses = [] } = payload
    const transformedData = transformToMapData(analyses)
    const result = {
      ...payload,
      analyses: transformedData
    }
    setData(result)
  }, [fetchMemberAnalyses])

  useEffect(() => {
    search()
  }, [fetchMemberAnalyses, search])

  const [periodStartDate, periodEndDate] = [-1, 0].map(year => {
    const reportDate = _.get(data, 'reportDateEnd')
    if (_.isEmpty(reportDate)) {
      return EMPTY_VALUE
    }
    if (year === 0) {
      return getUIFormatDate(moment(reportDate))
    }
    return getUIFormatDate(moment(reportDate).add('years', year).add('days', 1))
  })

  return (
    <Loader loaded={!isLoading}>
      {
        fetchError
          ? (
            <Error
              onClick={search}
              iconType='update'
              text='系統忙碌中，請稍後再試'
            />
          )
          : (
            <div>
              <Information description='會員：一年內有購買紀錄之買家' />
              <Information description={`資料統計區間：${periodStartDate} ~ ${periodEndDate}（每日中午更新）`} />
              <TaiwanMaps memberAnalysesData={_.get(data, 'analyses', [])} />
              <CountryList memberAnalysesData={_.get(data, 'analyses', [])} />
            </div>
          )
      }
    </Loader>
  )
}

// use <Recharts /> to draw bar chart with horizontal layout sorted by memberQuantity
const CountryList = ({ memberAnalysesData }) => (
  <div className={styles.barChart}>
    <ResponsiveContainer height={600}>
      <ComposedChart
        data={memberAnalysesData}
        layout='vertical'
        margin={{
          top: 20,
          right: 90,
          bottom: 20,
          left: 20
        }}
      >
        <CartesianGrid stroke='#f5f5f5' />
        <XAxis
          dataKey='memberQuantity'
          type='number'
        />
        <YAxis
          dataKey='typeName'
          type='category'
          scale="band"
          tick={{ dy: 10 }}
        />
        <Bar
          dataKey='memberQuantity'
          barSize={20}
        >
          <LabelList
            dataKey='memberQuantity'
            position='right'
            fill='#000'
          />
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  </div>
)

const mapStateToProps = state => ({
  formValues: _.get(state, `form.${CONFIG.form}.values`)
})

const MemberTrend = connect(mapStateToProps)(({ fetchMemberAnalyses }) => {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState(null)

  // default use the date range from 30 days ago to yesterday
  const customRange = DEFAULT_DASHBOARD_BESTSELLERS_DATE_RANGE

  const onSearch = useCallback(async (formValues) => {
    const { reportDate, type } = formValues
    setIsLoading(true)
    const [
      reportDateStart, reportDateEnd
    ] = [moment(reportDate[0]), moment(reportDate[1])].map(momentDate => getFormatData(momentDate, DATE_FORMAT.API))
    const [reqError, payload = {}] = await safeAwait(
      FetchDataUtil.fetchAllPages(
        fetchMemberAnalyses, {
          payload: {
            group: 'overview',
            reportDateStart,
            reportDateEnd,
            // ref. https://git.ouryahoo.com/TW-Shopping-BackOffice/component_shopping_spa_doc/blob/413cb7a77ffd60409fdc58083f85fea120affa98/content/api/resources/dashboard.mdx#L288
            type: _.isEmpty(type) ? '101,102,103' : type,
            limit: 50
          },
          selector: res => _.get(res, 'analyses', [])
        }
      )
    )
    setIsLoading(false)
    const errorMessage = errorHelper.parseErrorMessage(reqError)
    setFetchError(errorMessage)
    if (reqError) {
      return
    }

    const { data } = payload
    const transformedData = transformToChartData(data)
    setData(transformedData)
  }, [fetchMemberAnalyses])

  useEffect(() => {
    onSearch({ reportDate: customRange })
  }, [fetchMemberAnalyses, onSearch, customRange])

  return (
    <Loader loaded={!isLoading}>
      {
        fetchError
          ? (
            <Error
              onClick={onSearch}
              iconType='update'
              text='系統忙碌中，請稍後再試'
            />
          )
          : (
            <>
              <Block key='searchForm'>
                <SearchForm
                  tip='可查詢最早日期為 2023/12/01，起迄區間最長為六個月，每日中午更新'
                  customRange={customRange}
                  onFormInit={() => null}
                  onSubmit={onSearch}
                />
              </Block>
              <Block key='tradingCharts'>
                <MemberOverviewDescription />
                <TrendCharts
                  data={data}
                />
              </Block>
            </>
          )
      }
    </Loader>
  )
})

export const transformToChartData = data => {
  const transformedData = data.reduce((acc, item) => {
    const { reportDate, type, memberQuantity } = item
    const newAcc = { ...acc }
    newAcc[reportDate] = {
      ...newAcc[reportDate],
      reportDate,
      [type === 101 ? TRANSFORMED_FIELD.memberQuantity : type === 102 ? TRANSFORMED_FIELD.newMemberQuantity : TRANSFORMED_FIELD.returnMemberQuantity]: memberQuantity
    }
    return newAcc
  }, {})
  return Object.values(transformedData)
}

export default MemberAnalyses
